import React, { useState, useCallback, ReactNode } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '../button/Button';
import { LoadingButton } from '../button/LoadingButton';

export interface WizardStep {
  id: string;
  title: string;
  content: ReactNode;
  validate?: () => boolean | Promise<boolean>;
}

export interface WizardProps {
  steps: WizardStep[];
  currentStep: number;
  onStepChange: (step: number) => void;
  onComplete: () => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  showStepIndicator?: boolean;
  showStepTitles?: boolean;
  nextButtonText?: string;
  backButtonText?: string;
  completeButtonText?: string;
  cancelButtonText?: string;
  canGoBack?: boolean;
  errorMessage?: string | null;
  /** If true, footer will not be rendered inline. Use with BaseModal footer prop and renderWizardFooter helper */
  externalFooter?: boolean;
  className?: string;
}

/**
 * A flexible wizard component for multi-step flows
 * 
 * @example
 * ```tsx
 * const steps: WizardStep[] = [
 *   {
 *     id: 'step1',
 *     title: 'Personal Info',
 *     content: <Step1Content />,
 *     validate: async () => email.trim().length > 0
 *   },
 *   {
 *     id: 'step2',
 *     title: 'Select Options',
 *     content: <Step2Content />
 *   }
 * ];
 * 
 * <Wizard
 *   steps={steps}
 *   currentStep={currentStep}
 *   onStepChange={setCurrentStep}
 *   onComplete={handleComplete}
 *   onCancel={handleCancel}
 * />
 * ```
 */
export function Wizard({
  steps,
  currentStep,
  onStepChange,
  onComplete,
  onCancel,
  isLoading = false,
  showStepIndicator = true,
  showStepTitles = true, // Keep for backward compatibility but not used in new UI
  nextButtonText = 'Next',
  backButtonText = 'Back',
  completeButtonText = 'Complete',
  cancelButtonText = 'Cancel',
  canGoBack = true,
  errorMessage = null,
  externalFooter = false,
  className = ''
}: WizardProps) {
  // Suppress unused var warning for showStepTitles (kept for backward compatibility)
  void showStepTitles;
  const [isValidating, setIsValidating] = useState(false);

  const currentStepData = steps[currentStep - 1];
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === steps.length;

  const handleNext = useCallback(async () => {
    if (currentStepData?.validate) {
      setIsValidating(true);
      try {
        const isValid = await currentStepData.validate();
        if (!isValid) {
          setIsValidating(false);
          return;
        }
      } catch (error) {
        console.error('Validation error:', error);
        setIsValidating(false);
        return;
      }
      setIsValidating(false);
    }

    if (isLastStep) {
      await onComplete();
    } else {
      onStepChange(currentStep + 1);
    }
  }, [currentStep, currentStepData, isLastStep, onComplete, onStepChange]);

  const handleBack = useCallback(() => {
    if (!isFirstStep && canGoBack) {
      onStepChange(currentStep - 1);
    }
  }, [currentStep, isFirstStep, canGoBack, onStepChange]);

  return (
    <div className={`flex flex-col min-h-0 ${className}`}>
      {/* Step Indicator */}
      {showStepIndicator && (
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-4">
              {steps.map((step, index) => {
                const stepNumber = index + 1;
                const isCompleted = stepNumber < currentStep;

                return (
                  <React.Fragment key={step.id}>
                    {index > 0 && (
                      <div 
                        className={`w-12 h-0.5 ${
                          stepNumber <= currentStep 
                            ? 'bg-primary' 
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`} 
                      />
                    )}
                    
                    <div 
                      className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        stepNumber <= currentStep
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        stepNumber
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Error Message - appears below step indicator */}
      {errorMessage && (
        <div className="px-6 pt-4">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="text-sm font-medium text-red-800 dark:text-red-200">
              {errorMessage}
            </div>
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="p-6 overflow-y-auto">
        {currentStepData?.content}
      </div>

      {/* Footer - only render inline if externalFooter is false */}
      {!externalFooter && (
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          {!isFirstStep && canGoBack ? (
            <Button 
              variant="outline" 
              onClick={handleBack}
              disabled={isLoading || isValidating}
            >
              {backButtonText}
            </Button>
          ) : (
            <Button 
              variant="outline" 
              onClick={onCancel}
              disabled={isLoading || isValidating}
            >
              {cancelButtonText}
            </Button>
          )}

          <LoadingButton
            onClick={handleNext}
            loading={isLoading || isValidating}
            loadingText={isValidating ? 'Validating...' : 'Processing...'}
            disabled={isLoading || isValidating}
          >
            {isLastStep ? completeButtonText : nextButtonText}
          </LoadingButton>
        </div>
      )}
    </div>
  );
}

/**
 * Helper function to render wizard footer buttons
 * Use this when you want to place the footer outside the Wizard (e.g., in BaseModal footer)
 */
export function renderWizardFooter(props: {
  isFirstStep: boolean;
  isLastStep: boolean;
  canGoBack: boolean;
  isLoading: boolean;
  isValidating: boolean;
  handleBack: () => void;
  handleNext: () => void;
  onCancel: () => void;
  backButtonText: string;
  nextButtonText: string;
  completeButtonText: string;
  cancelButtonText: string;
}): ReactNode {
  const {
    isFirstStep,
    isLastStep,
    canGoBack,
    isLoading,
    isValidating,
    handleBack,
    handleNext,
    onCancel,
    backButtonText,
    nextButtonText,
    completeButtonText,
    cancelButtonText
  } = props;

  return (
    <div className="flex items-center justify-end gap-3">
      {!isFirstStep && canGoBack ? (
        <Button 
          variant="outline" 
          onClick={handleBack}
          disabled={isLoading || isValidating}
        >
          {backButtonText}
        </Button>
      ) : (
        <Button 
          variant="outline" 
          onClick={onCancel}
          disabled={isLoading || isValidating}
        >
          {cancelButtonText}
        </Button>
      )}

      <LoadingButton
        onClick={handleNext}
        loading={isLoading || isValidating}
        loadingText={isValidating ? 'Validating...' : 'Processing...'}
        disabled={isLoading || isValidating}
      >
        {isLastStep ? completeButtonText : nextButtonText}
      </LoadingButton>
    </div>
  );
}

/**
 * Hook to get wizard state and handlers for rendering footer externally
 * Use with renderWizardFooter helper and BaseModal footer prop
 */
export function useWizardFooter(props: {
  steps: WizardStep[];
  currentStep: number;
  onStepChange: (step: number) => void;
  onComplete: () => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  canGoBack?: boolean;
  nextButtonText?: string;
  backButtonText?: string;
  completeButtonText?: string;
  cancelButtonText?: string;
}) {
  const {
    steps,
    currentStep,
    onStepChange,
    onComplete,
    onCancel,
    isLoading = false,
    canGoBack = true,
    nextButtonText = 'Next',
    backButtonText = 'Back',
    completeButtonText = 'Complete',
    cancelButtonText = 'Cancel'
  } = props;

  const [isValidating, setIsValidating] = useState(false);
  const currentStepData = steps[currentStep - 1];
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === steps.length;

  const handleNext = useCallback(async () => {
    if (currentStepData?.validate) {
      setIsValidating(true);
      try {
        const isValid = await currentStepData.validate();
        if (!isValid) {
          setIsValidating(false);
          return;
        }
      } catch (error) {
        console.error('Validation error:', error);
        setIsValidating(false);
        return;
      }
      setIsValidating(false);
    }

    if (isLastStep) {
      await onComplete();
    } else {
      onStepChange(currentStep + 1);
    }
  }, [currentStep, currentStepData, isLastStep, onComplete, onStepChange]);

  const handleBack = useCallback(() => {
    if (!isFirstStep && canGoBack) {
      onStepChange(currentStep - 1);
    }
  }, [currentStep, isFirstStep, canGoBack, onStepChange]);

  return {
    isFirstStep,
    isLastStep,
    canGoBack,
    isLoading,
    isValidating,
    handleBack,
    handleNext,
    onCancel,
    backButtonText,
    nextButtonText,
    completeButtonText,
    cancelButtonText
  };
}